import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { gql } from "../../../__generated__";
import type {
  DeleteSellerScheduledPushOptOutMutation,
  DeleteSellerScheduledPushOptOutMutationVariables,
  GetStoreListQuery,
  SellerScheduledPushOptOutsQuery,
  UpsertSellerScheduledPushOptOutMutation,
  UpsertSellerScheduledPushOptOutMutationVariables,
} from "../../../__generated__/graphql";
import { notify, notifyError } from "../../../utils/toast";
import LoadingSpinnerInline from "../../../components/common/loading-spinner/loading-spinner-inline";
import ResponsiveCDNImage from "../../../components/common/image/responsive-cdn-image";
import { AdminListDetailLayout } from "../components/admin-list-detail-layout";
import {
  AdminStoreTableWithMobileCards,
  type AdminStoreListItem,
} from "../components/admin-store-table-with-mobile-cards";
import { useScrollToDetailPanelOnSelect } from "../hooks/use-scroll-to-detail-panel";
import { GET_STORE_LIST } from "../store-signup-request/page";

const SELLER_SCHEDULED_PUSH_OPT_OUTS = gql(`
  query SellerScheduledPushOptOuts {
    sellerScheduledPushOptOuts {
      id
      sellerId
      reason
      createdAt
      updatedAt
    }
  }
`);

const UPSERT_SELLER_SCHEDULED_PUSH_OPT_OUT = gql(`
  mutation UpsertSellerScheduledPushOptOut($sellerId: Int!, $reason: String) {
    upsertSellerScheduledPushOptOut(sellerId: $sellerId, reason: $reason) {
      ok
      error
    }
  }
`);

const DELETE_SELLER_SCHEDULED_PUSH_OPT_OUT = gql(`
  mutation DeleteSellerScheduledPushOptOut($sellerId: Int!) {
    deleteSellerScheduledPushOptOut(sellerId: $sellerId) {
      ok
      error
    }
  }
`);

type StoreRow = NonNullable<
  GetStoreListQuery["getStoreList"]
>["result"][number];

type OptOutRow =
  SellerScheduledPushOptOutsQuery["sellerScheduledPushOptOuts"][number];

function toStoreListItem(store: StoreRow): AdminStoreListItem {
  return {
    id: store.id,
    title: store.title,
    img: store.img,
    address: store.address,
  };
}

export default function AdminSellerScheduledPushOptOutPage() {
  const [keyword, setKeyword] = useState("");
  const [selectedStoreId, setSelectedStoreId] = useState<number | null>(null);
  const [reasonInput, setReasonInput] = useState("");

  const { data, loading, error, refetch: refetchStores } =
    useQuery<GetStoreListQuery>(GET_STORE_LIST);
  const {
    data: optOutData,
    loading: optOutLoading,
    refetch: refetchOptOuts,
  } = useQuery<SellerScheduledPushOptOutsQuery>(SELLER_SCHEDULED_PUSH_OPT_OUTS);

  const [upsertOptOut, { loading: upserting }] = useMutation<
    UpsertSellerScheduledPushOptOutMutation,
    UpsertSellerScheduledPushOptOutMutationVariables
  >(UPSERT_SELLER_SCHEDULED_PUSH_OPT_OUT);

  const [deleteOptOut, { loading: deleting }] = useMutation<
    DeleteSellerScheduledPushOptOutMutation,
    DeleteSellerScheduledPushOptOutMutationVariables
  >(DELETE_SELLER_SCHEDULED_PUSH_OPT_OUT);

  const optOutBySellerId = useMemo(() => {
    const rows = optOutData?.sellerScheduledPushOptOuts ?? [];
    const m = new Map<number, OptOutRow>();
    for (const row of rows) {
      m.set(row.sellerId, row);
    }
    return m;
  }, [optOutData?.sellerScheduledPushOptOuts]);

  const approvedAll = useMemo(() => {
    const list =
      data?.getStoreList?.ok ? (data.getStoreList.result ?? []) : [];
    return list.filter((s) => s.signupState === "승인");
  }, [data?.getStoreList?.ok, data?.getStoreList?.result]);

  const keywordLower = keyword.trim().toLowerCase();
  const stores = keywordLower
    ? approvedAll.filter(
        (s) =>
          s.title.toLowerCase().includes(keywordLower) ||
          (s.address?.toLowerCase().includes(keywordLower) ?? false) ||
          (s.seller?.name?.toLowerCase().includes(keywordLower) ?? false) ||
          (s.seller?.contactNumber?.toLowerCase().includes(keywordLower) ??
            false)
      )
    : approvedAll;

  const selectedRow = selectedStoreId
    ? approvedAll.find((s) => s.id === selectedStoreId) ?? null
    : null;

  useScrollToDetailPanelOnSelect(
    selectedRow ? { id: selectedRow.id } : null,
  );

  const disabledStoreWithoutSellerIds = useMemo(
    () => approvedAll.filter((s) => s.seller == null).map((s) => s.id),
    [approvedAll]
  );

  const handleStoreSelect = (item: AdminStoreListItem) => {
    const row = approvedAll.find((s) => s.id === item.id);
    if (!row?.seller?.id) {
      notifyError("사장님 정보가 연결된 매장만 선택할 수 있습니다.");
      return;
    }
    const nextId = item.id === selectedStoreId ? null : item.id;
    setSelectedStoreId(nextId);
    if (nextId === null) {
      setReasonInput("");
      return;
    }
    const nextSellerId = approvedAll.find((s) => s.id === nextId)?.seller?.id;
    const opt =
      nextSellerId != null ? optOutBySellerId.get(nextSellerId) : undefined;
    setReasonInput(opt?.reason ?? "");
  };

  const refetchAll = async () => {
    await Promise.all([refetchStores(), refetchOptOuts()]);
  };

  const handleRegister = async () => {
    if (!selectedRow?.seller?.id) return;
    const res = await upsertOptOut({
      variables: {
        sellerId: selectedRow.seller.id,
        reason: reasonInput.trim() || null,
      },
    });
    if (res.data?.upsertSellerScheduledPushOptOut.ok) {
      notify("정기 푸시 제외가 등록되었습니다.");
      await refetchAll();
    } else {
      notifyError(
        res.data?.upsertSellerScheduledPushOptOut.error ??
          "등록에 실패했습니다."
      );
    }
  };

  const handleUpdateReasonWhileOptedOut = async () => {
    if (!selectedRow?.seller?.id || !optOutBySellerId.has(selectedRow.seller.id)) {
      return;
    }
    const res = await upsertOptOut({
      variables: {
        sellerId: selectedRow.seller.id,
        reason: reasonInput.trim() || null,
      },
    });
    if (res.data?.upsertSellerScheduledPushOptOut.ok) {
      notify("사유가 저장되었습니다.");
      await refetchAll();
    } else {
      notifyError(
        res.data?.upsertSellerScheduledPushOptOut.error ??
          "저장에 실패했습니다."
      );
    }
  };

  const handleRemove = async () => {
    if (!selectedRow?.seller?.id) return;
    const res = await deleteOptOut({
      variables: { sellerId: selectedRow.seller.id },
    });
    if (res.data?.deleteSellerScheduledPushOptOut.ok) {
      notify("제외가 해제되었습니다.");
      setReasonInput("");
      await refetchAll();
    } else {
      notifyError(
        res.data?.deleteSellerScheduledPushOptOut.error ??
          "해제에 실패했습니다."
      );
    }
  };

  if (loading || optOutLoading) {
    return (
      <div className="w-full p-4">
        <div className="flex justify-center py-8">
          <LoadingSpinnerInline />
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="w-full p-4">
        <div className="text-red-600">에러: {error.message}</div>
      </div>
    );
  }
  if (!data?.getStoreList?.ok) {
    return (
      <div className="w-full p-4">
        <div className="text-red-600">
          {data?.getStoreList?.error ?? "목록을 불러올 수 없습니다."}
        </div>
      </div>
    );
  }

  const sellerId = selectedRow?.seller?.id;
  const isOptedOut = sellerId != null && optOutBySellerId.has(sellerId);

  return (
    <AdminListDetailLayout
      title="사장님 정기 푸시 제외"
      description="평일 아침 응원·저녁 실시간 판매 리마인드 푸시를 받지 않을 사장님을 등록합니다."
      leftRatio={7}
      rightRatio={3}
      leftPanel={{
        title: "승인 매장",
        searchPlaceholder: "매장명·주소·사장님·연락처",
        searchValue: keyword,
        onSearchChange: setKeyword,
        children: (
          <AdminStoreTableWithMobileCards
            stores={stores.map(toStoreListItem)}
            selectionMode="single"
            selectedIds={selectedStoreId ? [selectedStoreId] : []}
            onSelect={handleStoreSelect}
            disabledStoreIds={disabledStoreWithoutSellerIds}
            extraHeaders={
              <>
                <th className="px-4 py-2 font-medium whitespace-nowrap">제외</th>
              </>
            }
            renderExtraCells={(store) => {
              const row = approvedAll.find((r) => r.id === store.id);
              const sid = row?.seller?.id;
              const opted =
                sid != null && optOutBySellerId.has(sid);
              return (
                <td className="px-4 py-2">
                  {sid == null ? (
                    <span className="text-xs text-gray-400">해당 없음</span>
                  ) : (
                    <span
                      className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${
                        opted
                          ? "bg-amber-100 text-amber-900"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {opted ? "제외됨" : "수신"}
                    </span>
                  )}
                </td>
              );
            }}
            renderExtraCard={(store) => {
              const row = approvedAll.find((r) => r.id === store.id);
              const sid = row?.seller?.id;
              const opted =
                sid != null && optOutBySellerId.has(sid);
              return (
                <div className="mt-2 text-xs text-gray-600">
                  {sid == null ? (
                    <span className="text-gray-400">사장님 미연동</span>
                  ) : (
                    <>
                      정기 푸시:{" "}
                      <span
                        className={opted ? "font-semibold text-amber-700" : ""}
                      >
                        {opted ? "제외됨" : "수신 중"}
                      </span>
                    </>
                  )}
                </div>
              );
            }}
            emptyText={
              keyword.trim()
                ? "검색 결과가 없습니다."
                : "승인된 매장이 없습니다."
            }
          />
        ),
      }}
      rightPanel={{
        title: "제외 등록 · 해제",
        emptyText: "좌측에서 매장을 선택하세요.",
        children:
          selectedRow && selectedRow.seller ? (
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                {selectedRow.img ? (
                  <ResponsiveCDNImage
                    src={selectedRow.img}
                    alt={selectedRow.title}
                    className="h-14 w-14 shrink-0 rounded object-cover"
                    sizeRatio={0.15}
                  />
                ) : (
                  <div className="h-14 w-14 shrink-0 rounded bg-gray-200" />
                )}
                <div className="min-w-0">
                  <div className="truncate font-semibold text-gray-900">
                    {selectedRow.title}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    seller #{selectedRow.seller.id} ·{" "}
                    {selectedRow.seller.name ?? "이름 미입력"} ·{" "}
                    {selectedRow.seller.contactNumber}
                  </div>
                </div>
              </div>

              <div
                className={`rounded-lg border px-3 py-2 text-sm ${
                  isOptedOut
                    ? "border-amber-200 bg-amber-50 text-amber-900"
                    : "border-gray-200 bg-gray-50 text-gray-700"
                }`}
              >
                현재 상태:{" "}
                <span className="font-medium">
                  {isOptedOut ? "두 정기 푸시 모두 발송 안 함" : "정상 수신 중"}
                </span>
              </div>

              <div>
                <label
                  htmlFor="opt-out-reason"
                  className="mb-1 block text-xs font-medium text-gray-600"
                >
                  사유 (선택·내부 기록용)
                </label>
                <textarea
                  id="opt-out-reason"
                  value={reasonInput}
                  onChange={(e) => setReasonInput(e.target.value)}
                  rows={4}
                  className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="예: 업주 요청, 반복 안내 종료 등"
                  disabled={upserting || deleting}
                />
              </div>

              {!isOptedOut ? (
                <button
                  type="button"
                  onClick={() => void handleRegister()}
                  disabled={upserting || deleting}
                  className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {upserting ? "처리 중…" : "제외 등록"}
                </button>
              ) : (
                <div className="flex flex-col gap-2 sm:flex-row">
                  <button
                    type="button"
                    onClick={() => void handleUpdateReasonWhileOptedOut()}
                    disabled={
                      upserting ||
                      deleting ||
                      reasonInput ===
                        (optOutBySellerId.get(selectedRow.seller.id)?.reason ??
                          "")
                    }
                    className="flex-1 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {upserting ? "처리 중…" : "사유 저장"}
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleRemove()}
                    disabled={upserting || deleting}
                    className="flex-1 rounded-md bg-gray-200 px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {deleting ? "처리 중…" : "제외 해제"}
                  </button>
                </div>
              )}
            </div>
          ) : selectedRow && !selectedRow.seller ? (
            <p className="text-sm text-amber-700">
              이 매장에는 연결된 사장님(seller)이 없어 제외를 설정할 수 없습니다.
            </p>
          ) : null,
      }}
    />
  );
}
